/**
 * ErrorBoundary — globalny łapacz błędów renderowania React.
 *
 * React 19 nadal wymaga klasy (getDerivedStateFromError nie ma odpowiednika
 * w hookach). Używamy go w dwóch miejscach:
 *  1. `main.tsx` — top-level (pełnoekranowy fallback, łapie absolutnie wszystko)
 *  2. `TenantLayout.tsx` — per-treść (sidebar zostaje, upada tylko obszar content)
 *
 * Fallback produkuje polskojęzyczny komunikat (hardcoded — celowo, żeby nie
 * zależeć od i18n w momencie awarii). W trybie DEV dodatkowo wyświetla stack trace.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

// --------------------------------------------------------------------------
// Typy
// --------------------------------------------------------------------------

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Opcjonalny customowy renderer fallbacku.
   * Dostaje error + callback resetu (próba ponownego wyrenderowania dzieci).
   * Jeśli nie podany — używa DefaultErrorFallback.
   */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// --------------------------------------------------------------------------
// ErrorBoundary
// --------------------------------------------------------------------------

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Docelowo: wysłać do Sentry / monitoring (F15+).
    // Na razie: console.error widoczny w devtools + logach production.
    console.error('[ErrorBoundary] Uncaught render error:', error.message);
    console.error('[ErrorBoundary] Component stack:', info.componentStack);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error) {
      if (fallback) return fallback(error, this.reset);
      return <DefaultErrorFallback error={error} onReset={this.reset} />;
    }

    return children;
  }
}

// --------------------------------------------------------------------------
// DefaultErrorFallback — używany gdy brak customowego `fallback` prop
// --------------------------------------------------------------------------

interface FallbackProps {
  error: Error;
  onReset: () => void;
}

function DefaultErrorFallback({ error, onReset }: FallbackProps) {
  const isDev = import.meta.env.DEV;

  return (
    <div
      className="flex min-h-[400px] flex-1 flex-col items-center justify-center gap-4 p-8 text-center"
      role="alert"
      aria-live="assertive"
    >
      {/* Ikona ostrzeżenia */}
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
        <svg
          className="h-8 w-8 text-red-600 dark:text-red-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          />
        </svg>
      </div>

      {/* Komunikat */}
      <div>
        <h2 className="text-lg font-semibold text-text-primary">Coś poszło nie tak</h2>
        <p className="mt-1 max-w-sm text-sm text-text-secondary">
          Wystąpił nieoczekiwany błąd. Spróbuj odświeżyć widok lub całą stronę.
        </p>
      </div>

      {/* Stack trace (tylko DEV) */}
      {isDev && (
        <pre className="max-h-60 max-w-xl overflow-auto rounded-lg bg-gray-100 p-4 text-left text-xs text-red-700 dark:bg-gray-800 dark:text-red-400">
          {error.message}
          {error.stack ? '\n\n' + error.stack : ''}
        </pre>
      )}

      {/* Akcje */}
      <div className="flex gap-3">
        <button
          onClick={onReset}
          className="rounded-lg bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-accent-500"
        >
          Spróbuj ponownie
        </button>
        <button
          onClick={() => window.location.reload()}
          className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-primary hover:bg-bg-secondary focus:outline-none focus:ring-2 focus:ring-accent-500"
        >
          Odśwież stronę
        </button>
      </div>
    </div>
  );
}
